import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { Table, Button, Alert, Popconfirm, Popover, Space, Tooltip, message } from 'antd';
import { EditOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import UserAvatar from '../common/UserAvatar';
import DepartmentModal from './modals/DepartmentModal';
import { deleteDepartment } from './api';

const DepartmentsPanel = forwardRef(({ companyId, departments }, ref) => {
    const { data, loading, error, page, setPage, refetch } = departments;
    const selectedCompanyData = useSelector((state) => state.company?.selectedCompanyData);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingDept, setEditingDept] = useState(null);

    useImperativeHandle(ref, () => ({
        openAddModal: () => { setEditingDept(null); setModalOpen(true); },
    }));

    const handleDelete = async (id) => {
        try {
            await deleteDepartment(companyId, id);
            message.success('Deleted successfully');
            refetch();
        } catch (err) {
            message.error(err.response?.data?.message || 'Delete failed');
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'City / State',
            key: 'location',
            render: (_, record) => [record.city, record.state].filter(Boolean).join(', ') || '—',
        },
        {
            title: (
                <span>
                    Calendar user{' '}
                    <Tooltip title="User whose Cronofy calendar receives interview events scheduled for candidates in this department. Set from the Edit dialog. Falls back to the bot owner when empty.">
                        <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                    </Tooltip>
                </span>
            ),
            key: 'calendar_user',
            render: (_, record) => {
                const u = record.calendar_user_info;
                if (!u) return <span style={{ color: '#bfbfbf' }}>—</span>;
                return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <UserAvatar name={u.name} userId={u.id} src={u.avatar_url} size={20} showTooltip={false} />
                        <span>{u.name}</span>
                    </span>
                );
            },
        },
        {
            title: (
                <span>
                    Users{' '}
                    <Tooltip title="Number of users assigned to this department. Assigned users get notified when a candidate lands here and are available for interview scheduling. Manage the list from the Edit dialog.">
                        <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                    </Tooltip>
                </span>
            ),
            dataIndex: 'user_count',
            key: 'user_count',
            width: 90,
            render: (count, record) => {
                const members = record.assigned_users || [];
                if (!count || !members.length) {
                    return <span style={{ color: '#8c8c8c' }}>{count || 0}</span>;
                }
                const content = (
                    <div style={{ minWidth: 180 }}>
                        <div style={{
                            fontWeight: 600,
                            paddingBottom: 8,
                            marginBottom: 4,
                            borderBottom: '1px solid #f0f0f0',
                        }}>
                            Assigned users
                        </div>
                        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                            {members.map((u) => (
                                <div
                                    key={u.id}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}
                                >
                                    <UserAvatar name={u.name} userId={u.id} src={u.avatar_url} size={22} showTooltip={false} />
                                    <span>{u.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                );
                return (
                    <Popover
                        content={content}
                        placement="left"
                        trigger="hover"
                        mouseEnterDelay={0.2}
                    >
                        <span style={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
                            {count}
                        </span>
                    </Popover>
                );
            },
        },
        {
            title: '',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => { setEditingDept(record); setModalOpen(true); }}
                    />
                    <Popconfirm
                        title="Are you sure you want to delete this department?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

            <Table
                className="gh-dash-interview-table"
                columns={columns}
                dataSource={data.rows}
                loading={loading}
                rowKey="id"
                size="small"
                pagination={{
                    current: data.page,
                    pageSize: data.pageSize,
                    total: data.total,
                    onChange: (p) => setPage(p),
                    showSizeChanger: false,
                    size: 'small',
                }}
                locale={{
                    emptyText: !loading && data.total === 0 ? (
                        <div style={{ padding: 12 }}>
                            <p>No departments found.</p>
                            <Button type="primary" onClick={() => { setEditingDept(null); setModalOpen(true); }}>
                                + Add your first department
                            </Button>
                        </div>
                    ) : 'No departments found.',
                }}
            />

            <DepartmentModal
                open={modalOpen}
                department={editingDept}
                companyId={companyId}
                companyName={selectedCompanyData?.name || ''}
                onClose={() => { setModalOpen(false); setEditingDept(null); }}
                onSuccess={() => { setModalOpen(false); setEditingDept(null); refetch(); }}
            />
        </div>
    );
});

DepartmentsPanel.displayName = 'DepartmentsPanel';

export default DepartmentsPanel;
